/* eslint-disable camelcase */
import { decamelizeKeys } from "humps";
import { MatchingRelationType } from "PFApp/constants/matching_relation_type";
import api from "PFCore/api";
import { ApiRoute } from "PFCore/utilities/routes";
import { Activity, Collection, MatchedActivity, PaginatedCollection } from "PFTypes";
import { ActivityFeedbackCreateData } from "PFTypes/activity_feedback";
import { Id } from "PFTypes/id";
import { QueryParams } from "PFTypes/query_params";

import { fetchPaginatedCollection } from "../helpers/collection";
import { decamelizeDesiredKeys } from "../helpers/decamelize_desired_keys";

type ActivityResponseData = { id: Id; activity: Activity };

export type ActivitiesResponse = Collection<ActivityResponseData[]>;

export const fetchActivities = (params): Promise<ActivitiesResponse> =>
  api({
    url: "activities",
    method: "GET",
    params
  });

export const fetchActivity = (activityId: Id): Promise<Activity> =>
  // @ts-ignore
  $.ajax({
    url: ApiRoute(`/api/activities/${activityId}`),
    method: "GET",
    api_version: 2
  });

export const closeActivity = (activityId: number, closureReasons) =>
  // @ts-ignore
  $.ajax({
    url: ApiRoute(`/api/activities/${activityId}/close`),
    method: "PUT",
    api_version: 2,
    data: JSON.stringify({
      ...(closureReasons ? { vacancies_closure_reason: closureReasons } : {})
    }),
    contentType: "application/json; charset=utf-8"
  });

export const updateActivity = (activityId, data) =>
  api({
    url: `activities/${activityId}`,
    method: "PUT",
    data
  });

export const createActivity = (data) =>
  api({
    url: "activities",
    method: "POST",
    data
  }).then((resp) => decamelizeKeys(resp));

export const deleteActivity = (id) =>
  // @ts-ignore
  $.ajax({
    url: ApiRoute(`/api/activities/${id}`),
    method: "DELETE",
    api_version: 2
  });

export const forceManual = (activityId: number) =>
  api({
    url: `audit_roles/${activityId}/force_manual`,
    method: "PUT"
  });

export const fetchActivityWatchers = (activityId: number, page = 1, perPage = 10, filters = {}, order = {}) =>
  fetchPaginatedCollection(ApiRoute(`/api/activities/${activityId}/interests`), {
    perPage,
    page,
    filters,
    // @ts-ignore
    order
  });

export interface QuestionnaireAnswers {
  answer1?: boolean | null;
  answer2?: boolean | null;
  answer3?: boolean | null;
  statement?: string | null;
}

export type ShowInterestResponse = QuestionnaireAnswers & {
  id: number;
  activity_id: number;
  profile_id: number;
  createdAt: string;
};

export const showInterest = (
  activityId: number,
  questionnaireAnswers: QuestionnaireAnswers = {}
): Promise<ShowInterestResponse> =>
  // @ts-ignore
  $.ajax({
    url: ApiRoute(`/api/activities/${activityId}/interests`),
    method: "POST",
    api_version: 2,
    data: JSON.stringify(questionnaireAnswers),
    contentType: "application/json"
  });

export const removeInterest = (activityId: number, interestId: number): Promise<void> =>
  // @ts-ignore
  $.ajax({
    url: ApiRoute(`/api/activities/${activityId}/interests/${interestId}`),
    method: "DELETE",
    api_version: 2
  });

export const fetchActivityParticipants = (activityId: number) =>
  api({
    url: `activities/${activityId}/feedback_participants`,
    method: "GET"
  });

export const fetchActivityFeedback = (feedbackId: number) =>
  api({
    url: `feedback/${feedbackId}`,
    method: "GET"
  });

export const fetchProfileFeedback = (profileId: number, roleIds: number[]) =>
  api({
    url: "feedback",
    params: { profileId, roleIds }
  });

export const sendActivityFeedback = (data: ActivityFeedbackCreateData) =>
  api({
    url: "feedback",
    method: "POST",
    data
  });

export type MatchingActivitiesParams = QueryParams & {
  profileId: Id;
  relationType: MatchingRelationType;
};

export type MatchingActivitiesResponse = PaginatedCollection<MatchedActivity[]>;

export const fetchMatchingActivities = (data: MatchingActivitiesParams) =>
  api<MatchingActivitiesResponse>({
    url: "matching_activities",
    method: "POST",
    data
  }).then((res) => decamelizeDesiredKeys<MatchingActivitiesResponse>(res, ["entries", "meta.filters"])); // TODO: [ENG-1712] Remove decamelizeKeys when app is camelized

type AIDescriptionResponse = { roleDescription: string };

export const fetchGeneratedDescription = (body: {
  jobTitle: string;
  selectedSkills?: string[];
  roleDescription?: string;
  longer?: boolean;
  regenerate?: boolean;
}) =>
  api<AIDescriptionResponse>({
    method: "POST",
    url: "ai/generate_description",
    data: body
  });
